import { Container, Text, Accordion, Card, List, ThemeIcon, Divider, Tabs, Group, Badge } from '@mantine/core';
import { IconCircleCheck, IconAlertCircle, IconInfoCircle, IconScale, IconFileInvoice, IconTruck, IconAlertTriangle } from '@tabler/icons-react';
import PageTitle from '../../components/common/PageTitle';

export const RulesAndRegulations = () => {

    return (
        <div style={{ backgroundColor: '#f8fafc' }}>
            <Container size="lg" pt={20} pb={60}>
                <PageTitle
                    title="Rules and Regulations"
                    description="Important guidelines and regulations for using our parcel forwarding services safely and legally."
                    icon={<IconScale size={24} />}
                    breadcrumbs={[
                        { title: 'Dashboard', href: '/dashboard' },
                        { title: 'Rules and Regulations' }
                    ]}
                    withBorder
                    size="lg"
                />

                <Tabs defaultValue="prohibited" mt="xl">
                    <Tabs.List mb="lg">
                        <Tabs.Tab
                            value="prohibited"
                            leftSection={<IconAlertTriangle size="0.8rem" />}
                        >
                            Prohibited Items
                        </Tabs.Tab>
                        <Tabs.Tab
                            value="customs"
                            leftSection={<IconFileInvoice size="0.8rem" />}
                        >
                            Customs & Duties
                        </Tabs.Tab>
                        <Tabs.Tab
                            value="shipping"
                            leftSection={<IconTruck size="0.8rem" />}
                        >
                            Shipping Guidelines
                        </Tabs.Tab>
                        <Tabs.Tab
                            value="liability"
                            leftSection={<IconScale size="0.8rem" />}
                        >
                            Liability & Insurance
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="prohibited">
                        <Card withBorder p="lg" mb="md">
                            <Group mb="md">
                                <ThemeIcon color="red" size="lg" radius="xl">
                                    <IconAlertTriangle size="1.2rem" />
                                </ThemeIcon>
                                <Text fw={600} size="lg">Strictly Prohibited Items</Text>
                            </Group>

                            <Accordion>
                                <Accordion.Item value="dangerous">
                                    <Accordion.Control>
                                        <Group>
                                            <Badge color="red" variant="filled">DANGEROUS</Badge>
                                            <Text fw={500}>Hazardous Materials</Text>
                                        </Group>
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <List spacing="xs" size="sm">
                                            <List.Item icon={<ThemeIcon color="red" size={16} radius="xl"><IconAlertCircle size="0.8rem" /></ThemeIcon>}>
                                                Flammable liquids and gases
                                            </List.Item>
                                            <List.Item icon={<ThemeIcon color="red" size={16} radius="xl"><IconAlertCircle size="0.8rem" /></ThemeIcon>}>
                                                Explosives and fireworks
                                            </List.Item>
                                            <List.Item icon={<ThemeIcon color="red" size={16} radius="xl"><IconAlertCircle size="0.8rem" /></ThemeIcon>}>
                                                Toxic chemicals and poisons
                                            </List.Item>
                                            <List.Item icon={<ThemeIcon color="red" size={16} radius="xl"><IconAlertCircle size="0.8rem" /></ThemeIcon>}>
                                                Radioactive materials
                                            </List.Item>
                                        </List>
                                    </Accordion.Panel>
                                </Accordion.Item>

                                <Accordion.Item value="restricted">
                                    <Accordion.Control>
                                        <Group>
                                            <Badge color="orange" variant="filled">RESTRICTED</Badge>
                                            <Text fw={500}>Controlled Substances</Text>
                                        </Group>
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <List spacing="xs" size="sm">
                                            <List.Item icon={<ThemeIcon color="orange" size={16} radius="xl"><IconAlertCircle size="0.8rem" /></ThemeIcon>}>
                                                Prescription medications
                                            </List.Item>
                                            <List.Item icon={<ThemeIcon color="orange" size={16} radius="xl"><IconAlertCircle size="0.8rem" /></ThemeIcon>}>
                                                Tobacco products
                                            </List.Item>
                                            <List.Item icon={<ThemeIcon color="orange" size={16} radius="xl"><IconAlertCircle size="0.8rem" /></ThemeIcon>}>
                                                Alcohol and beverages
                                            </List.Item>
                                            <List.Item icon={<ThemeIcon color="orange" size={16} radius="xl"><IconAlertCircle size="0.8rem" /></ThemeIcon>}>
                                                Live animals and plants
                                            </List.Item>
                                        </List>
                                    </Accordion.Panel>
                                </Accordion.Item>

                                <Accordion.Item value="illegal">
                                    <Accordion.Control>
                                        <Group>
                                            <Badge color="dark" variant="filled">ILLEGAL</Badge>
                                            <Text fw={500}>Prohibited by Law</Text>
                                        </Group>
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <List spacing="xs" size="sm">
                                            <List.Item icon={<ThemeIcon color="dark" size={16} radius="xl"><IconAlertCircle size="0.8rem" /></ThemeIcon>}>
                                                Counterfeit goods and replicas
                                            </List.Item>
                                            <List.Item icon={<ThemeIcon color="dark" size={16} radius="xl"><IconAlertCircle size="0.8rem" /></ThemeIcon>}>
                                                Weapons and ammunition
                                            </List.Item>
                                            <List.Item icon={<ThemeIcon color="dark" size={16} radius="xl"><IconAlertCircle size="0.8rem" /></ThemeIcon>}>
                                                Stolen or illegally obtained items
                                            </List.Item>
                                            <List.Item icon={<ThemeIcon color="dark" size={16} radius="xl"><IconAlertCircle size="0.8rem" /></ThemeIcon>}>
                                                Items violating intellectual property
                                            </List.Item>
                                        </List>
                                    </Accordion.Panel>
                                </Accordion.Item>
                            </Accordion>
                        </Card>
                    </Tabs.Panel>

                    <Tabs.Panel value="customs">
                        <Card withBorder p="lg" mb="md">
                            <Group mb="md">
                                <ThemeIcon color="blue" size="lg" radius="xl">
                                    <IconFileInvoice size="1.2rem" />
                                </ThemeIcon>
                                <Text fw={600} size="lg">Customs and Duties Information</Text>
                            </Group>

                            <Text mb="md">
                                All international shipments are subject to customs inspection and duties.
                                You are responsible for understanding and complying with your country's import regulations.
                            </Text>

                            <List spacing="md" size="sm">
                                <List.Item icon={<ThemeIcon color="blue" size={16} radius="xl"><IconCircleCheck size="0.8rem" /></ThemeIcon>}>
                                    Accurate declaration of item values is mandatory
                                </List.Item>
                                <List.Item icon={<ThemeIcon color="blue" size={16} radius="xl"><IconCircleCheck size="0.8rem" /></ThemeIcon>}>
                                    Customs duties and taxes are paid by the recipient
                                </List.Item>
                                <List.Item icon={<ThemeIcon color="blue" size={16} radius="xl"><IconCircleCheck size="0.8rem" /></ThemeIcon>}>
                                    Under-declaring values may result in delays or penalties
                                </List.Item>
                                <List.Item icon={<ThemeIcon color="blue" size={16} radius="xl"><IconCircleCheck size="0.8rem" /></ThemeIcon>}>
                                    Some countries have specific restrictions on certain products
                                </List.Item>
                            </List>
                        </Card>
                    </Tabs.Panel>

                    <Tabs.Panel value="shipping">
                        <Card withBorder p="lg" mb="md">
                            <Group mb="md">
                                <ThemeIcon color="green" size="lg" radius="xl">
                                    <IconTruck size="1.2rem" />
                                </ThemeIcon>
                                <Text fw={600} size="lg">Shipping Guidelines</Text>
                            </Group>

                            <Text mb="md">
                                Follow these guidelines to ensure smooth processing and delivery of your packages.
                            </Text>

                            <Accordion>
                                <Accordion.Item value="packaging">
                                    <Accordion.Control>Package Requirements</Accordion.Control>
                                    <Accordion.Panel>
                                        <List spacing="xs" size="sm">
                                            <List.Item>Maximum weight per package: 30kg</List.Item>
                                            <List.Item>Maximum dimensions: 100cm x 100cm x 100cm</List.Item>
                                            <List.Item>Proper packaging required to prevent damage</List.Item>
                                            <List.Item>Fragile items must be clearly marked</List.Item>
                                        </List>
                                    </Accordion.Panel>
                                </Accordion.Item>

                                <Accordion.Item value="processing">
                                    <Accordion.Control>Processing Times</Accordion.Control>
                                    <Accordion.Panel>
                                        <List spacing="xs" size="sm">
                                            <List.Item>Standard processing: 1-3 business days</List.Item>
                                            <List.Item>Express processing: Same day (additional fee applies)</List.Item>
                                            <List.Item>Consolidation requests: 2-5 business days</List.Item>
                                            <List.Item>Peak seasons may extend processing times</List.Item>
                                        </List>
                                    </Accordion.Panel>
                                </Accordion.Item>
                            </Accordion>
                        </Card>
                    </Tabs.Panel>

                    <Tabs.Panel value="liability">
                        <Card withBorder p="lg" mb="md">
                            <Group mb="md">
                                <ThemeIcon color="purple" size="lg" radius="xl">
                                    <IconScale size="1.2rem" />
                                </ThemeIcon>
                                <Text fw={600} size="lg">Liability and Insurance</Text>
                            </Group>

                            <Text mb="md">
                                Understanding our liability coverage and insurance options for your packages.
                            </Text>

                            <List spacing="md" size="sm">
                                <List.Item icon={<ThemeIcon color="purple" size={16} radius="xl"><IconInfoCircle size="0.8rem" /></ThemeIcon>}>
                                    Basic coverage included up to $100 per package
                                </List.Item>
                                <List.Item icon={<ThemeIcon color="purple" size={16} radius="xl"><IconInfoCircle size="0.8rem" /></ThemeIcon>}>
                                    Additional insurance available for high-value items
                                </List.Item>
                                <List.Item icon={<ThemeIcon color="purple" size={16} radius="xl"><IconInfoCircle size="0.8rem" /></ThemeIcon>}>
                                    Claims must be reported within 30 days of delivery
                                </List.Item>
                                <List.Item icon={<ThemeIcon color="purple" size={16} radius="xl"><IconInfoCircle size="0.8rem" /></ThemeIcon>}>
                                    Proof of value required for insurance claims
                                </List.Item>
                            </List>

                            <Divider my="md" />

                            <Text size="sm" c="dimmed">
                                By using our services, you agree to these terms and conditions.
                                For complete terms of service, please contact our support team.
                            </Text>
                        </Card>
                    </Tabs.Panel>
                </Tabs>
            </Container>
        </div>
    );
};

export default RulesAndRegulations;
